/**
 * Admin Members Management Page
 *
 * Allows super admins to view and manage platform members.
 * Provides role assignment and member search capabilities.
 *
 * Features:
 * - View all members with pagination
 * - Search and filter members
 * - Assign roles (member, group_admin, super_admin)
 * - View member details
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router';
import { useAuth, useIsSuperAdmin } from '@/hooks/useAuth';
import { usersApi, ApiClientError } from '@/services/api';
import type { User, PaginationMeta, UserRole } from '@/types';

/**
 * Role badge component
 */
function RoleBadge({ role }: { role: UserRole }) {
  const roleStyles = {
    member: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    group_admin: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    super_admin: 'bg-primary/20 text-primary border-primary/30',
  };

  // spec-130 phase 7: 'group_admin' (system-wide bypass role) is now
  // labeled "Platform Moderator" to disambiguate from per-group
  // GroupMembership.role='admin'. Backend enum value untouched.
  const roleLabels = {
    member: 'Member',
    group_admin: 'Platform Moderator',
    super_admin: 'Super Admin',
  };

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded border ${roleStyles[role]}`}>
      {roleLabels[role]}
    </span>
  );
}

/**
 * Verification status indicator
 */
function VerificationStatus({ emailVerified, phoneVerified }: { emailVerified: boolean; phoneVerified: boolean }) {
  return (
    <div className="flex items-center gap-2">
      {emailVerified && (
        <span className="flex items-center gap-1 text-xs text-green-400">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Email
        </span>
      )}
      {phoneVerified && (
        <span className="flex items-center gap-1 text-xs text-green-400">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Phone
        </span>
      )}
      {!emailVerified && !phoneVerified && (
        <span className="text-xs text-white/40">Not verified</span>
      )}
    </div>
  );
}

/**
 * Loading spinner component
 */
function LoadingSpinner({ size = 'md' }: { size?: 'sm' | 'md' }) {
  const sizeClasses = size === 'sm' ? 'h-4 w-4' : 'h-8 w-8';

  return (
    <svg className={`animate-spin ${sizeClasses} text-primary`} viewBox="0 0 24 24">
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
        fill="none"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

/**
 * Empty state component
 */
function EmptyState({ searchQuery }: { searchQuery: string }) {
  return (
    <div className="text-center py-16">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/5 mb-4">
        <svg className="w-8 h-8 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
      </div>
      <h3 className="text-xl font-semibold text-white mb-2">
        {searchQuery ? 'No matching members' : 'No members yet'}
      </h3>
      <p className="text-white/60">
        {searchQuery
          ? 'Try adjusting your search criteria.'
          : 'Members will appear here once they sign up.'}
      </p>
    </div>
  );
}

/**
 * Pagination component
 */
interface PaginationProps {
  pagination: PaginationMeta;
  onPageChange: (page: number) => void;
}

function Pagination({ pagination, onPageChange }: PaginationProps) {
  if (pagination.totalPages <= 1) {
    return null;
  }

  return (
    <div className="flex items-center justify-center gap-4 mt-8">
      <button
        type="button"
        onClick={() => onPageChange(pagination.page - 1)}
        disabled={!pagination.hasPrev}
        className="btn-secondary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      <span className="text-white/60">
        Page {pagination.page} of {pagination.totalPages}
      </span>

      <button
        type="button"
        onClick={() => onPageChange(pagination.page + 1)}
        disabled={!pagination.hasNext}
        className="btn-secondary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  );
}

/**
 * Role change modal component
 */
interface RoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  member: User | null;
  onChangeRole: (userId: string, role: UserRole) => Promise<void>;
}

function RoleModal({ isOpen, onClose, member, onChangeRole }: RoleModalProps) {
  const [selectedRole, setSelectedRole] = useState<UserRole>('member');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (member) {
      setSelectedRole(member.role);
    }
  }, [member]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!member) return;

    try {
      setIsSaving(true);
      setError(null);
      await onChangeRole(member.id, selectedRole);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update role');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen || !member) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-secondary border border-white/10 rounded-2xl w-full max-w-md mx-4 p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-display font-semibold text-white">
            Change Role
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-white/60 hover:text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="mb-6">
          <p className="text-white/80 mb-2">
            Update role for <span className="text-white font-medium">{member.displayName}</span>
          </p>
          <p className="text-white/60 text-sm">{member.email}</p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="space-y-3 mb-6">
            <label className="flex items-center gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 cursor-pointer transition-colors">
              <input
                type="radio"
                name="role"
                value="member"
                checked={selectedRole === 'member'}
                onChange={() => setSelectedRole('member')}
                className="w-4 h-4 text-primary"
                disabled={isSaving}
              />
              <div>
                <p className="text-white font-medium">Member</p>
                <p className="text-white/60 text-sm">Basic access to member features</p>
              </div>
            </label>

            <label className="flex items-center gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 cursor-pointer transition-colors">
              <input
                type="radio"
                name="role"
                value="group_admin"
                checked={selectedRole === 'group_admin'}
                onChange={() => setSelectedRole('group_admin')}
                className="w-4 h-4 text-primary"
                disabled={isSaving}
              />
              <div>
                <p className="text-white font-medium">Platform Moderator</p>
                <p className="text-white/60 text-sm">Bypasses per-group authz; can manage every group + content platform-wide.</p>
              </div>
            </label>

            <label className="flex items-center gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 cursor-pointer transition-colors">
              <input
                type="radio"
                name="role"
                value="super_admin"
                checked={selectedRole === 'super_admin'}
                onChange={() => setSelectedRole('super_admin')}
                className="w-4 h-4 text-primary"
                disabled={isSaving}
              />
              <div>
                <p className="text-white font-medium">Super Admin</p>
                <p className="text-white/60 text-sm">Full administrative access</p>
              </div>
            </label>
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary px-6"
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary px-6 flex items-center gap-2"
              disabled={isSaving || selectedRole === member.role}
            >
              {isSaving ? (
                <>
                  <LoadingSpinner size="sm" />
                  Saving...
                </>
              ) : (
                'Update Role'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Format date for display
 */
function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Admin Members Page Component
 */
export function MembersPage() {
  const navigate = useNavigate();
  const { isLoading: authLoading, isAuthenticated } = useAuth();
  const isSuperAdmin = useIsSuperAdmin();

  // State
  const [members, setMembers] = useState<User[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState<UserRole | 'all'>('all');
  const [selectedMember, setSelectedMember] = useState<User | null>(null);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);

  const ITEMS_PER_PAGE = 20;

  /**
   * Fetch members
   */
  const fetchMembers = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await usersApi.list({
        page: currentPage,
        limit: ITEMS_PER_PAGE,
      });

      setMembers(response.data);
      setPagination(response.pagination);
    } catch (err) {
      const errorMessage = err instanceof ApiClientError
        ? err.message
        : 'Failed to load members';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage]);

  /**
   * Initial load
   */
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      fetchMembers();
    }
  }, [authLoading, isAuthenticated, fetchMembers]);

  /**
   * Redirect non-admin users
   */
  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !isSuperAdmin)) {
      navigate('/login', { state: { from: '/admin/members' }, replace: true });
    }
  }, [authLoading, isAuthenticated, isSuperAdmin, navigate]);

  /**
   * Handle page change
   */
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  /**
   * Filter members based on search and role
   */
  const filteredMembers = useMemo(() => {
    return members.filter((member) => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch =
          member.displayName.toLowerCase().includes(query) ||
          (member.email && member.email.toLowerCase().includes(query)) ||
          (member.phone && member.phone.includes(query));
        if (!matchesSearch) return false;
      }

      // Role filter
      if (filterRole !== 'all') {
        return member.role === filterRole;
      }

      return true;
    });
  }, [members, searchQuery, filterRole]);

  /**
   * Handle role change
   */
  const handleRoleChange = useCallback(async (userId: string, role: UserRole) => {
    try {
      await usersApi.updateRole(userId, role);

      // Update local state
      setMembers(prev => prev.map(m =>
        m.id === userId ? { ...m, role } : m
      ));

      setSuccessMessage('Role updated successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      throw err;
    }
  }, []);

  /**
   * Open role modal for a member
   */
  const openRoleModal = useCallback((member: User) => {
    setSelectedMember(member);
    setIsRoleModalOpen(true);
  }, []);

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="container mx-auto px-4 py-16">
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  // Don't render anything if not authorized (will redirect)
  if (!isAuthenticated || !isSuperAdmin) {
    return null;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-display font-bold">
            <span className="text-gradient">Members</span>
          </h1>
          <p className="text-white/60 mt-1">
            Manage platform members and assign roles
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/admin/groups" className="btn-secondary flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            Manage Groups
          </Link>
          <Link to="/admin" className="btn-secondary">
            Back to Admin
          </Link>
        </div>
      </div>

      {/* Stats Row */}
      {pagination && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10 text-primary border border-primary/20">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div>
                <p className="text-white/60 text-sm">Total Members</p>
                <p className="text-2xl font-bold text-white">{pagination.total}</p>
              </div>
            </div>
          </div>
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10 text-blue-400 border border-blue-500/20">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div>
                <p className="text-white/60 text-sm">Regular Members</p>
                <p className="text-2xl font-bold text-white">
                  {members.filter(m => m.role === 'member').length}
                </p>
              </div>
            </div>
          </div>
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-yellow-500/10 text-yellow-400 border border-yellow-500/20">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <div>
                <p className="text-white/60 text-sm">Platform Moderators</p>
                <p className="text-2xl font-bold text-white">
                  {members.filter(m => m.role === 'group_admin').length}
                </p>
              </div>
            </div>
          </div>
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10 text-primary border border-primary/20">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div>
                <p className="text-white/60 text-sm">Super Admins</p>
                <p className="text-2xl font-bold text-white">
                  {members.filter(m => m.role === 'super_admin').length}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Banner */}
      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-3">
          <svg className="w-5 h-5 text-red-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {/* Success Banner */}
      {successMessage && (
        <div className="mb-6 p-4 bg-green-500/10 border border-green-500/20 rounded-lg flex items-center gap-3">
          <svg className="w-5 h-5 text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <p className="text-green-400">{successMessage}</p>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="flex-1">
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input w-full pl-10"
            />
          </div>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setFilterRole('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filterRole === 'all'
                ? 'bg-primary text-white'
                : 'bg-white/5 text-white/60 hover:bg-white/10'
            }`}
          >
            All
          </button>
          <button
            type="button"
            onClick={() => setFilterRole('member')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filterRole === 'member'
                ? 'bg-blue-500 text-white'
                : 'bg-white/5 text-white/60 hover:bg-white/10'
            }`}
          >
            Members
          </button>
          <button
            type="button"
            onClick={() => setFilterRole('group_admin')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filterRole === 'group_admin'
                ? 'bg-yellow-500 text-white'
                : 'bg-white/5 text-white/60 hover:bg-white/10'
            }`}
          >
            Platform Moderators
          </button>
          <button
            type="button"
            onClick={() => setFilterRole('super_admin')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filterRole === 'super_admin'
                ? 'bg-primary text-white'
                : 'bg-white/5 text-white/60 hover:bg-white/10'
            }`}
          >
            Super Admins
          </button>
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : filteredMembers.length === 0 ? (
        <EmptyState searchQuery={searchQuery} />
      ) : (
        <>
          {/* Members Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-3 px-4 text-white/60 text-sm font-medium">Member</th>
                  <th className="text-left py-3 px-4 text-white/60 text-sm font-medium hidden md:table-cell">Email</th>
                  <th className="text-left py-3 px-4 text-white/60 text-sm font-medium hidden lg:table-cell">Verified</th>
                  <th className="text-left py-3 px-4 text-white/60 text-sm font-medium">Role</th>
                  <th className="text-left py-3 px-4 text-white/60 text-sm font-medium hidden xl:table-cell">Joined</th>
                  <th className="text-left py-3 px-4 text-white/60 text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredMembers.map((member) => (
                  <tr
                    key={member.id}
                    className="border-b border-white/5 hover:bg-white/5 transition-colors"
                  >
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                          {member.avatarUrl ? (
                            <img
                              src={member.avatarUrl}
                              alt={member.displayName}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <span className="text-primary font-medium">
                              {member.displayName.charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <p className="text-white font-medium">{member.displayName}</p>
                      </div>
                    </td>
                    <td className="py-4 px-4 hidden md:table-cell">
                      <p className="text-white/80">{member.email || '-'}</p>
                    </td>
                    <td className="py-4 px-4 hidden lg:table-cell">
                      <VerificationStatus
                        emailVerified={member.emailVerified}
                        phoneVerified={member.phoneVerified}
                      />
                    </td>
                    <td className="py-4 px-4">
                      <RoleBadge role={member.role} />
                    </td>
                    <td className="py-4 px-4 hidden xl:table-cell">
                      <p className="text-white/60 text-sm">{formatDate(member.createdAt)}</p>
                    </td>
                    <td className="py-4 px-4">
                      <button
                        type="button"
                        onClick={() => openRoleModal(member)}
                        className="text-primary hover:text-primary-light transition-colors text-sm font-medium"
                      >
                        Change Role
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && (
            <Pagination
              pagination={pagination}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}

      {/* Role Modal */}
      <RoleModal
        isOpen={isRoleModalOpen}
        onClose={() => {
          setIsRoleModalOpen(false);
          setSelectedMember(null);
        }}
        member={selectedMember}
        onChangeRole={handleRoleChange}
      />
    </div>
  );
}

export default MembersPage;
